import { FormEvent, useEffect, useState } from "react";
import { confirmPasswordMatch, validatePassword } from "@/lib/validators";

import { Button } from "@/components/ui/button";
import { CardWrapper } from "@/components/card-wrapper";
import { Check } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInputComponent } from "@/components/ui/password-input";
import { RequestSource } from "@/lib/errorHandling";
import { toast } from "sonner";
import { useHandleError } from "@/hooks/useHandleError";
import { useParams } from "react-router";
import { usePocketBase } from "@/context/pocketbase-context";
import { useTranslation } from "react-i18next";

export function PasswordResetPage() {
  const { token } = useParams<{ token: string }>();
  const { t } = useTranslation(["pages", "common"]);
  const { handleError } = useHandleError();
  const { changePassword } = usePocketBase();
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [isFormValid, setIsFormValid] = useState(false);
  const [passwordChangeSuccess, setPasswordChangeSuccess] = useState(false);

  useEffect(() => {
    setIsFormValid(
      password !== "" &&
        confirmPassword !== "" &&
        passwordError === "" &&
        confirmPasswordError === "",
    );
  }, [password, confirmPassword, passwordError, confirmPasswordError]);

  const handlePasswordChange = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!token) {
      toast.error(t("pages:passwordReset.invalidTokenToast"));
      return;
    }

    validatePassword(password, setPasswordError);
    confirmPasswordMatch(password, confirmPassword, setConfirmPasswordError);
    if (!isFormValid) {
      return;
    }
    try {
      const res = await changePassword(token, password, confirmPassword);
      if (res) {
        toast.success(t("pages:passwordReset.passwordResetSuccessToast"));
        setPasswordChangeSuccess(true);
      }
    } catch (error) {
      toast.error(handleError(RequestSource.CHANGE_PASSWORD, error));
    }
  };

  if (passwordChangeSuccess) {
    return (
      <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
        <CardWrapper className="w-[380px] md:w-[450px]">
          <div className="space-y-6 p-4 px-8 text-center">
            <div className="space-y-4">
              <h2 className="mt-6 text-3xl font-bold text-foreground">
                {t("pages:passwordReset.passwordResetSuccessTitle")}
              </h2>
              <p className="text-gray-500 dark:text-gray-400">
                {t("pages:passwordReset.passwordResetSuccessMessage")}
              </p>
            </div>
            <div className="my-10">
              <div className="flex justify-center animate-in fade-in zoom-in duration-500">
                <div className="rounded-full bg-green-500 p-6">
                  <Check className="w-16 h-16 text-white" />
                </div>
              </div>
            </div>
            <a
              href="/login"
              className="block text-sm underline text-foreground"
            >
              {t("common:buttons.backToLogin")}
            </a>
          </div>
        </CardWrapper>
      </div>
    );
  }

  return (
    <div className="pt-12 flex justify-center items-center w-screen h-screen p-4">
      <CardWrapper className="w-[380px] md:w-[450px]">
        <div className="space-y-6 p-4 px-8">
          <div className="space-y-2 text-center pb-10">
            <h2 className="mt-6 text-3xl font-bold text-foreground">
              {t("pages:passwordReset.passwordResetTitle")}
            </h2>
            <p className="hidden md:inline text-gray-500 dark:text-gray-400">
              {t("pages:passwordReset.passwordResetSubtitle")}
            </p>
          </div>
          <form className="mt-8 space-y-6" onSubmit={handlePasswordChange}>
            <div className="space-y-4 rounded-md shadow-sm">
              <div>
                <Label htmlFor="password">{t("common:password")}</Label>
                <PasswordInputComponent
                  id="password"
                  name="password"
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    validatePassword(e.target.value, setPasswordError);
                  }}
                  className={passwordError ? "border-red-500" : ""}
                />
                {passwordError && (
                  <p className="text-sm text-red-500">{passwordError}</p>
                )}
              </div>
              <div>
                <Label htmlFor="confirm-password">
                  {t("common:passwordConfirmation")}
                </Label>
                <Input
                  id="confirm-password"
                  name="confirm-password"
                  type="password"
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    confirmPasswordMatch(
                      password,
                      e.target.value,
                      setConfirmPasswordError,
                    );
                  }}
                  className={confirmPasswordError ? "border-red-500" : ""}
                />
                {confirmPasswordError && (
                  <p className="text-sm text-red-500">{confirmPasswordError}</p>
                )}
              </div>
            </div>

            <div>
              <Button type="submit" className="w-full" disabled={!isFormValid}>
                {t("pages:passwordReset.resetButton")}
              </Button>
            </div>
          </form>

          {/* <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t("pages:loginSignup.orContinueWith")}
              </span>
            </div>
          </div> */}
          {/* <SocialAuth handleSocialAuth={handleSocialSignUp} /> */}
        </div>
      </CardWrapper>
    </div>
  );
}
